import React, { useMemo } from 'react';
import Proptypes from 'prop-types';

import { XRayChartStyle } from './XRayChartStyle';
import roundToPrecision from '../../utils/roundToPrecision';
import { useSelector } from 'react-redux';
import translate from '../../libs/i18n';

import { colors } from '../../ui/variables';

function hexToRgb(hex) {
  // Remove the hash sign if it exists
  const onlyHex = hex.replace('#', '');
  // Split the hex string into three 2-character components
  const r_hex = onlyHex.substring(0, 2);
  const g_hex = onlyHex.substring(2, 4);
  const b_hex = onlyHex.substring(4, 6);
  // Convert each component from hexadecimal to a decimal integer (base 16)
  const r = parseInt(r_hex, 16);
  const g = parseInt(g_hex, 16);
  const b = parseInt(b_hex, 16);
  // Return the RGB values
  return {'red': r, 'green': g, 'blue': b};
}

function colorGradient(fadeFraction, rgbColor1, rgbColor2) {
    var color1 = rgbColor1;
    var color2 = rgbColor2;
    var fade = fadeFraction;
    var diffRed = color2.red - color1.red;
    var diffGreen = color2.green - color1.green;
    var diffBlue = color2.blue - color1.blue;
    var gradient = {
      red: parseInt(Math.floor(color1.red + (diffRed * fade)), 10),
      green: parseInt(Math.floor(color1.green + (diffGreen * fade)), 10),
      blue: parseInt(Math.floor(color1.blue + (diffBlue * fade)), 10),
    };
    return 'rgb(' + gradient.red + ',' + gradient.green + ',' + gradient.blue + ')';
}

function formattedClassification(classification, language) {
  return translate(classification, language);
}

const XRayChart = ({ percent, classification, chartOnly }) => {
  const language = useSelector((state) => state.settings.language);
  const translatedClassification = formattedClassification(classification, language);

  const precisionPercent = useMemo(
    () => roundToPrecision(percent, 7),
    [percent]
  );

  const circleBackgroundColor = useMemo(() => {
    if (precisionPercent && classification == "Normal"){
      return [`linear-gradient(90deg, ${colors.brightBlue} 0%,  ${colors.softBlue} 100%)`,
              colorGradient(precisionPercent, hexToRgb(colors.brightBlue), hexToRgb(colors.softBlue))      
              ]
    }
    else if (precisionPercent && classification == "Abnormal"){
      return [`linear-gradient(90deg, ${colors.lightOrange} 50%, ${colors.brightRed} 100%)`,
              colorGradient(precisionPercent, hexToRgb(colors.lightOrange), hexToRgb(colors.brightRed))
            ]
    }
    else {
      return ''
    }
  }, [precisionPercent, classification]);
  
  var pointerValue = precisionPercent * 100;
  return (
    <XRayChartStyle className={chartOnly ? 'chart-only' : ''}>
      {!chartOnly ? (
        <div className="x-ray-scale">
          <div className="x-ray-scale-colors"
          style={{ background: circleBackgroundColor[0] }}
          >
            <div
              className="x-ray-scale-pointer"
              style={{ left: `${pointerValue}%` }}
            >{`Score: ${(precisionPercent * 100).toFixed(1)}`}</div>
          </div>
        </div>
      ) : null}
      <div className="x-ray-content-wrapper">
        <div className="x-ray-content">
          <div className="circles-wrapper">
            <div
              className="chart-circle-color"
              style={{ background: circleBackgroundColor[1] }}
            />
            <div className="chart-circle-value">
              {!chartOnly ? (
                <div className="circle-value">{(precisionPercent * 100).toFixed(1)}</div>
              ) : null}
            </div>
          </div>
        </div>
        {!chartOnly && classification && classification.length ? (
          <div className="x-ray-classification">{translatedClassification}</div>
        ) : null}
      </div>
    </XRayChartStyle>
  );
};

XRayChart.propTypes = {
  chartOnly: Proptypes.bool,
  percent: Proptypes.number.isRequired,
  classification: Proptypes.string,
};

XRayChart.defaultProps = {
  chartOnly: false,
  classification: '',
};

export default XRayChart;
